'use client';
import React, { useState } from 'react';
import { Bell, Menu, Search, FileWarning } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface TopbarProps {
  title: string;
  subtitle?: string;
  onOpenMobile: () => void;
}

const notifications: Array<{ id: string; icon: typeof FileWarning; text: string; time: string; tone: 'danger' | 'success' | 'gold' | 'neutral' }> = [];

const toneClass = {
  danger: 'bg-destructive/10 text-destructive',
  success: 'bg-success/10 text-success',
  gold: 'bg-accent/25 text-primary',
  neutral: 'bg-muted text-muted-foreground',
};

export default function Topbar({ title, subtitle, onOpenMobile }: TopbarProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border bg-background/90 px-4 backdrop-blur md:px-6 lg:px-8 2xl:px-16">
      <div className="flex min-w-0 items-center gap-3">
        <button type="button" onClick={onOpenMobile} style={{ touchAction: 'manipulation' }} className="btn-ghost flex h-11 w-11 items-center justify-center !p-2 lg:hidden" aria-label="Open navigation">
          <Menu size={20} />
        </button>
        <div className="min-w-0">
          <h1 className="truncate text-lg font-semibold leading-tight text-foreground md:text-xl">{title}</h1>
          {subtitle && <p className="hidden truncate text-xs text-muted-foreground sm:block">{subtitle}</p>}
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        <div className="relative hidden md:block">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search actions, sessions…"
            aria-label="Search"
            className="field-input !w-64 !py-2 !pl-9 !pr-12 xl:!w-80"
          />
          <kbd className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">⌘K</kbd>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Notifications"
            aria-expanded={open}
            className="btn-ghost relative !p-2.5"
          >
            <Bell size={19} />
            <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-destructive ring-2 ring-background" />
          </button>
          {open && (
            <>
              <button type="button" aria-label="Close notifications" onClick={() => setOpen(false)} className="fixed inset-0 z-40 cursor-default" />
              <div className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] animate-scale-in overflow-hidden rounded-xl border border-border bg-card shadow-xl">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                  <span className="text-sm font-semibold text-card-foreground">Notifications</span>
                  <button type="button" onClick={() => setOpen(false)} className="text-xs font-semibold text-primary hover:underline">Mark all read</button>
                </div>
                <ul className="max-h-80 divide-y divide-border overflow-y-auto scroll-thin">
                  {notifications.length === 0 && (
                    <li className="px-4 py-8 text-center text-sm text-muted-foreground">No notifications yet.</li>
                  )}
                  {notifications.map((n) => {
                    const Icon = n.icon;
                    return (
                      <li key={n.id} className="flex gap-3 px-4 py-3 transition-colors duration-150 hover:bg-muted/60">
                        <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${toneClass[n.tone]}`}>
                          <Icon size={15} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-card-foreground">{n.text}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">{n.time}</p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}